import { useState, useCallback, useMemo } from 'react';

type UseImageSelectionOptions = {
    maxSelected?: number;
};

type UseImageSelectionResult = {
    isSelecting: boolean;
    selectedImageIds: Set<number>;
    isSelectionFull: boolean;
    startSelection: () => void;
    cancelSelection: () => void;
    toggleImageSelection: (id: number) => void;
    selectAllImages: (ids: number[]) => void;
    deselectAllImages: () => void;
};

export function useImageSelection({
    maxSelected
}: UseImageSelectionOptions = {}): UseImageSelectionResult {
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectedImageIds, setSelectedImageIds] = useState<Set<number>>(new Set());

    const isSelectionFull = useMemo(
        () => maxSelected !== undefined && selectedImageIds.size >= maxSelected,
        [selectedImageIds, maxSelected]
    );

    const startSelection = useCallback(() => {
        setIsSelecting(true);
    }, []);

    const cancelSelection = useCallback(() => {
        setIsSelecting(false);
        setSelectedImageIds(new Set());
    }, []);

    const toggleImageSelection = useCallback(
        (id: number) => {
            setSelectedImageIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else if (maxSelected === undefined || next.size < maxSelected) {
                    next.add(id);
                }
                return next;
            });
        },
        [maxSelected]
    );

    const selectAllImages = useCallback(
        (ids: number[]) => {
            setSelectedImageIds(
                new Set(maxSelected === undefined ? ids : ids.slice(0, maxSelected))
            );
        },
        [maxSelected]
    );

    const deselectAllImages = useCallback(() => {
        setSelectedImageIds(new Set());
    }, []);

    return {
        isSelecting,
        selectedImageIds,
        isSelectionFull,
        startSelection,
        cancelSelection,
        toggleImageSelection,
        selectAllImages,
        deselectAllImages
    };
}
